import React, { useEffect, useState, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const ToolDetails = () => {
  const { slug } = useParams();
  const navigate = useNavigate();

  const [tool, setTool] = useState(null);
  const [isScriptLoaded, setIsScriptLoaded] = useState(false);
  const [isFavorite, setIsFavorite] = useState(false);
  const [loading, setLoading] = useState(true);
  const [userEmail, setUserEmail] = useState("");
  const [hasPurchased, setHasPurchased] = useState(false);

  // Check if user has already purchased the tool
  const checkPurchaseStatus = useCallback(async (email, toolName, token) => {
    if (!email || !toolName || !token) return;

    try {
      const orderRes = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/orders`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
          params: {
            "filters[userEmail][$eq]": email,
            "filters[status][$eq]": "successful",
          },
        }
      );

      // Check if orders exist and is an array
      if (orderRes.data && Array.isArray(orderRes.data)) {
        const purchased = orderRes.data.some((order) => {
          return (
            order.status === "successful" &&
            order.tool &&
            order.tool.toolName === toolName &&
            order.userEmail === email
          );
        });

        setHasPurchased(purchased);
      } else {
        // Handle case where orders is a single object
        const order = orderRes.data;
        const purchased =
          order.status === "successful" &&
          order.tool &&
          order.tool.toolName === toolName &&
          order.userEmail === email;

        setHasPurchased(purchased);
      }
    } catch (error) {
      console.error("Error checking purchase status:", error);
    }
  }, []);

  // Fetch user details and check favorite status
  const fetchUserAndCheckFavorite = useCallback(
    async (token) => {
      try {
        const userRes = await axios.get(
          `${process.env.REACT_APP_API_URL}/api/users/me`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );

        const userData = userRes.data;
        setUserEmail(userData.email);

        // Check if tool is in wishlist
        const wishlistRes = await axios.get(
          `${process.env.REACT_APP_API_URL}/api/wishlists`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
            params: {
              "filters[email][$eq]": userData.email,
              "filters[slug][$eq]": slug,
            },
          }
        );

        setIsFavorite(
          wishlistRes.data.data && wishlistRes.data.data.length > 0
        );

        return userData.email;
      } catch (error) {
        console.error("Error fetching user and wishlist status:", error);
        return null;
      } finally {
        setLoading(false);
      }
    },
    [slug]
  );

  // Fetch tool details
  const fetchToolDetails = useCallback(
    async (token, currentEmail) => {
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_API_URL}/api/tools`,
          {
            params: {
              "filters[slug][$eq]": slug,
            },
          }
        );

        if (response.data.data && response.data.data.length > 0) {
          const toolItem = response.data.data[0];
          const toolData = { id: toolItem.id, ...(toolItem.attributes || toolItem) };
          setTool(toolData);
          console.log("Fetched tool:", toolData);

          // Check purchase status if we have both email and tool
          if (currentEmail && token) {
            await checkPurchaseStatus(
              currentEmail,
              toolData.name,
              token
            );
          }
        }
      } catch (error) {
        console.error("Error fetching tool details:", error);
      }
    },
    [slug, checkPurchaseStatus]
  );

  // razorpay and detail fetch
  useEffect(() => {
    const initializeData = async () => {
      // Load Razorpay script
      const script = document.createElement("script");
      script.src = "https://checkout.razorpay.com/v1/checkout.js";
      script.async = true;
      script.onload = () => setIsScriptLoaded(true);
      document.body.appendChild(script);

      const token = localStorage.getItem("jwt");
      if (token) {
        const email = await fetchUserAndCheckFavorite(token);
        await fetchToolDetails(token, email);
      } else {
        setLoading(false);
        await fetchToolDetails(null, null);
      }

      return () => {
        document.body.removeChild(script);
      };
    };

    initializeData();
  }, [fetchUserAndCheckFavorite, fetchToolDetails]);

  // Handle favorite toggle
  const handleFavoriteToggle = async () => {
    const token = localStorage.getItem("jwt");

    if (!token) {
      alert("Please log in to add to wishlist.");
      return;
    }

    setLoading(true);

    try {
      if (isFavorite) {
        // Find and delete the wishlist entry
        const findRes = await axios.get(
          `${process.env.REACT_APP_API_URL}/api/wishlists?filters[email][$eq]=${userEmail}&filters[slug][$eq]=${slug}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );

        if (findRes.data.data && findRes.data.data.length > 0) {
          const wishlistId = findRes.data.data[0].id;

          await axios.delete(
            `${process.env.REACT_APP_API_URL}/api/wishlists/${wishlistId}`,
            {
              headers: {
                Authorization: `Bearer ${token}`,
              },
            }
          );

          setIsFavorite(false);
          toast.error("Removed from wishlist!", {
            position: "top-right",
            autoClose: 3000,
          });
        }
      } else {
        // Add to wishlist
        const addData = {
          data: {
            email: userEmail,
            slug: slug,
            tool: tool.id,
          },
        };

        await axios.post(
          `${process.env.REACT_APP_API_URL}/api/wishlists`,
          addData,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );

        setIsFavorite(true);
        toast.success("Added to wishlist!", {
          position: "top-right",
          autoClose: 3000,
        });
      }
    } catch (error) {
      console.error("Error toggling wishlist:", error);
      alert("Failed to update wishlist. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  // handle download function
  const handleDownload = async () => {
    if (!isScriptLoaded) {
      alert("Payment system is still loading. Please try again in a moment.");
      return;
    }

    const token = localStorage.getItem("jwt");

    if (!tool.price) {
      toast.success("Tool downloading!", {
        position: "top-right",
        autoClose: 3000,
      });
      return;
    }

    if (!token) {
      alert("Please log in to proceed with the purchase.");
      return;
    }

    try {
      // Fetch user details
      const userRes = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/users/me`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      const userData = userRes.data;
      const userEmail = userData.email;

      // Create order
      const orderRes = await axios.post(
        `${process.env.REACT_APP_API_URL}/api/createOrder`,
        {
          amount: Number(tool.price) * 100,
          currency: "INR",
          receipt: `${tool.name}-receipt`,
          tool: {
            toolName: tool.name,
            toolImage: tool.image,
          },
          userEmail: userEmail,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      const orderData = orderRes.data;

      if (!orderData.id) {
        throw new Error("Failed to create order");
      }

      const options = {
        key: "rzp_test_yCsNcxlsD8WqnC",
        amount: orderData.amount,
        currency: orderData.currency,
        name: "PPDDEH",
        description: `Purchase of ${tool.name}`,
        order_id: orderData.id,
        handler: async function (response) {
          try {
            const verifyRes = await axios.post(
              `${process.env.REACT_APP_API_URL}/api/verifyPayment`,
              response,
              {
                headers: {
                  Authorization: `Bearer ${token}`,
                },
              }
            );

            const verifyData = verifyRes.data;
            if (verifyData.success) {
              toast.success("Tool purchased!", {
                position: "top-right",
                autoClose: 3000,
              });
            } else {
              console.error("Payment verification failed");
              navigateAfterSuccess();
            }
          } catch (error) {
            console.error("Verification request failed", error);
            navigateAfterSuccess();
          }
        },
        prefill: {
          email: userEmail,
        },
        theme: {
          color: "#3B82F6",
        },
      };

      const razorpayInstance = new window.Razorpay(options);
      razorpayInstance.open();
    } catch (error) {
      console.error("Error during payment:", error);
      toast.error(
        "There was an error processing your payment. Please try again.",
        {
          position: "top-right",
          autoClose: 3000,
        }
      );
    }
  };

  // navigate after success download
  const navigateAfterSuccess = () => {
    navigate("/our-tools");
    window.location.reload();
  };

  // handle purchased re-download
  const purchasedDownload = () => {
    toast.success("Tool downloading!", {
      position: "top-right",
      autoClose: 3000,
    });
  };

  // handle add to cart
  const handleAddToCart = async () => {
    const token = localStorage.getItem("jwt");

    if (!token) {
      alert("Please log in to add to cart.");
      return;
    }

    try {
      // Fetch user details
      const userRes = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/users/me`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      const userData = userRes.data;

      // Check if item already exists in cart
      const checkCartRes = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/carts?filters[email][$eq]=${userData.email}&filters[slug][$eq]=${slug}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (checkCartRes.data.data && checkCartRes.data.data.length > 0) {
        toast.error("Item already in cart!", {
          position: "top-right",
          autoClose: 3000,
        });
        return;
      }

      // Add to cart
      const addToCartData = {
        data: {
          email: userData.email,
          slug: slug,
          tool: tool.id,
        },
      };

      const addToCartRes = await axios.post(
        `${process.env.REACT_APP_API_URL}/api/carts`,
        addToCartData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (addToCartRes.data) {
        toast.success("Successfully added to cart!", {
          position: "top-right",
          autoClose: 3000,
        });
      }
    } catch (error) {
      console.error("Error adding to cart:", error);
      alert("Failed to add to cart. Please try again.");
    }
  };

  if (!tool) return <div>Loading...</div>;

  return (
    <div className="max-w-7xl mx-auto px-6 py-12">
      <h1 className="text-3xl font-semibold text-center mb-6">
        {tool.name}
      </h1>

      {/* Tool Details */}
      <div className="flex justify-center mb-12">
        <div className="w-full max-w-2xl">
          <img
            src={tool.image}
            alt={tool.name}
            className="w-full h-64 object-contain mb-6"
          />

          <p className="text-gray-700 mb-4">{tool.description}</p>

          <h2 className="text-xl font-semibold mb-2 text-gray-700 mt-[50px]">
            Requirements:
          </h2>
          <p className="text-gray-700 mb-4">{tool.requirements}</p>

          <div className="flex justify-between mt-6">
            <span className="text-gray-600">{tool.ratings} ★</span>

            {tool.price ? (
              <span className="text-gray-600">₹ {tool.price}</span>
            ) : (
              <span className="text-gray-600">Free</span>
            )}
          </div>
        </div>
      </div>

      {/* Download Button */}
      <div className="text-center">
        {hasPurchased ? (
          <div className="flex flex-col sm:flex-row justify-center items-center mt-4 space-y-4 sm:space-y-0 sm:space-x-6">
            <button
              onClick={handleFavoriteToggle}
              className="text-yellow-500 hover:text-yellow-400 transition duration-300"
              disabled={loading}
            >
              {isFavorite ? (
                <span className="text-3xl">★</span>
              ) : (
                <span className="text-3xl">☆</span>
              )}
            </button>
            <span className="text-green-600 font-semibold text-lg">
              Purchased
            </span>
            <button
              onClick={purchasedDownload}
              className="px-6 py-3 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition"
            >
              Download
            </button>
          </div>
        ) : tool.price ? (
          <div className="flex flex-col sm:flex-row justify-center items-center mt-4 space-y-4 sm:space-y-0 sm:space-x-6">
            <button
              onClick={handleFavoriteToggle}
              className="text-yellow-500 hover:text-yellow-400 transition duration-300"
              disabled={loading}
            >
              {isFavorite ? (
                <span className="text-3xl">★</span>
              ) : (
                <span className="text-3xl">☆</span>
              )}
            </button>

            <button
              onClick={handleDownload}
              className="px-6 py-3 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition"
            >
              Download ₹ {tool.price}
            </button>

            <button
              onClick={handleAddToCart}
              className="px-6 py-3 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition"
            >
              Add to Cart
            </button>
          </div>
        ) : (
          <div className="flex flex-col sm:flex-row justify-center items-center mt-4 space-y-4 sm:space-y-0 sm:space-x-6">
            <button
              onClick={handleFavoriteToggle}
              className="text-yellow-500 hover:text-yellow-400 transition duration-300"
              disabled={loading}
            >
              {isFavorite ? (
                <span className="text-3xl">★</span>
              ) : (
                <span className="text-3xl">☆</span>
              )}
            </button>

            <button
              onClick={handleDownload}
              className="px-6 py-3 ml-6 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition"
            >
              Download
            </button>
          </div>
        )}
      </div>

      <ToastContainer />
    </div>
  );
};

export default ToolDetails;
